import ExclusiveReleaseBadge from "@/components/ui/ExclusiveReleaseBadge";
import HeartButton from "@/components/ui/HeartButton";
import { getIsExclusive } from "@/lib/config/album-overrides";
import { ListAlbumDto } from "@/lib/lutely-api/gen/dtos";
import { useLibrary } from "@/lib/lutely-api/hooks";
import { albumTrackCountLabel, cn } from "@/lib/utils";
import { Box, Flex, Text } from "@radix-ui/themes";
import { AlbumLink } from "../links/AlbumLink";

export type ArtistAlbumCardProps = {
  album: ListAlbumDto;
  className?: string;
};

function formatAlbumPrice(album: ListAlbumDto) {
  if (album.pricingModel === "PAY_WHAT_YOU_WANT") {
    if (album.price == null || album.price <= 0) {
      return "Free";
    }
    return `$${album.price.toFixed(2)}+`;
  }

  if (album.price == null || album.price <= 0) {
    return "Free";
  }

  return `$${album.price.toFixed(2)}`;
}

export default function ArtistAlbumCard({
  album,
  className,
}: ArtistAlbumCardProps) {
  const priceLabel = formatAlbumPrice(album);
  const isExclusive = getIsExclusive(album.albumId);
  const { isAlbumSaved } = useLibrary();
  const saved = isAlbumSaved(album.albumId);

  return (
    <AlbumLink
      album={album}
      aria-label={`View album ${album.title}`}
      className={cn(
        "group block overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-black/5 transition-shadow hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 cursor-pointer",
        className,
      )}
    >
      <Box className="relative w-full aspect-square">
        <Box
          className="bg-no-repeat bg-center bg-cover h-full w-full object-cover"
          style={{
            backgroundImage: `url("${album.artworkUrl || "/placeholders/album_art_placeholder.png"}")`,
          }}
        />

        {isExclusive && (
          <ExclusiveReleaseBadge
            hoverExpand
            className="absolute top-2 left-2 z-10 scale-90"
          />
        )}

        <Flex
          className="pointer-events-none absolute top-0 left-0 right-0 bottom-0 bg-black/20 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
          aria-hidden="true"
        />

        <HeartButton
          kind="album"
          id={album.albumId}
          className={cn(
            "absolute !m-0 top-1 right-1 z-10 text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.6)] hover:bg-black/25 transition-opacity duration-200",
            !saved && "opacity-0 group-hover:opacity-100",
          )}
        />
      </Box>

      <Flex direction="column" className="p-4 w-full">
        <Text className="font-semibold text-blue-900 text-[16px] line-clamp-2">
          {album.title}
        </Text>

        <Text className="text-[#525966] font-semibold text-[14px] truncate mt-[2px]">
          {albumTrackCountLabel(album)}
        </Text>

        <Text className="text-blue-500 font-semibold text-[15px] mt-[2px]">
          {priceLabel}
        </Text>
      </Flex>
    </AlbumLink>
  );
}
